import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../request/http-common';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import CakeIcon from '@mui/icons-material/Cake';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';

export default function ViewApplication() {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [application, setApplication] = useState(null);
    const [status, setStatus] = useState('');
    const [notes, setNotes] = useState('');
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [migrationEligibility, setMigrationEligibility] = useState(null);
    const [showMigrationDialog, setShowMigrationDialog] = useState(false);
    const [migrationSuccess, setMigrationSuccess] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        formData: true,
        documents: true,
        timeline: false
    });

    const { isLoading: isLoadingApplication, refetch: getApplication } = useQuery(
        ["load-application", id],
        async () => {
            return await apiClient.get(`/api/applications/${id}`);
        },
        {
            enabled: false,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setApplication(res.data.data);
                    setStatus(res.data.data.status);
                    setNotes(res.data.data.notes || '');
                    const event2 = new Event('processed');
                    window.dispatchEvent(event2);
                }
            },
            onError: (err) => {
                console.log(err.response?.data || err);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
        }
    );

    const updateStatusMutation = useMutation(
        async (data) => {
            return await apiClient.patch(`/api/applications/${id}/status`, data);
        },
        {
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setUpdateSuccess(true);
                    getApplication(); // Refresh data
                    checkEligibility(); // Refresh migration eligibility
                    setTimeout(() => setUpdateSuccess(false), 3000);
                }
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
            onError: (err) => {
                console.log(err.response?.data || err);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
        }
    );

    const migrationMutation = useMutation(
        async () => {
            return await apiClient.post(`/api/applications/${id}/migrate-to-user`);
        },
        {
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setMigrationSuccess(true);
                    setShowMigrationDialog(false);
                    getApplication(); // Refresh data
                    checkEligibility(); // Refresh migration eligibility
                    setTimeout(() => setMigrationSuccess(false), 5000);
                }
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
            onError: (err) => {
                console.log(err.response?.data || err);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
        }
    );

    const { refetch: checkEligibility } = useQuery(
        ["migration-eligibility", id],
        async () => {
            return await apiClient.get(`/api/applications/${id}/migration-eligibility`);
        },
        {
            enabled: false,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setMigrationEligibility(res.data.data);
                }
            },
            onError: (err) => {
                console.log(err.response?.data || err);
            },
        }
    );

    useEffect(() => {
        getApplication();
        checkEligibility();
        if (isLoadingApplication) {
            const event3 = new Event('processing');
            window.dispatchEvent(event3);
        }
    }, [id]);

    const getDate = (date) => new Date(date).toLocaleDateString();
    const getDateTime = (date) => new Date(date).toLocaleString();

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'processing': return 'info';
            case 'completed': return 'primary';
            default: return 'default';
        }
    };

    const handleStatusUpdate = () => {
        if (updateStatusMutation.isLoading) return;
        
        const event3 = new Event('processing');
        window.dispatchEvent(event3);
        
        updateStatusMutation.mutate({
            status: status,
            notes: notes
        });
    };

    const handleMigrationConfirm = () => {
        if (migrationMutation.isLoading) return;
        
        const event3 = new Event('processing');
        window.dispatchEvent(event3);
        
        migrationMutation.mutate();
    };

    const getMigrationButtonText = () => {
        if (!migrationEligibility) return 'Grant Login Access';
        
        if (migrationEligibility.user_exists) return 'User Account Exists';
        if (!migrationEligibility.is_approved) return 'Approve First';
        if (application?.status === 'migrated_to_user') return 'Login Access Granted';
        
        return 'Grant Login Access';
    };

    const isMigrationDisabled = () => {
        if (!migrationEligibility) return true;
        return !migrationEligibility.can_migrate || migrationMutation.isLoading;
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const formatFieldName = (fieldName) => {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    const isFileField = (fieldName) => {
        const fileFields = [
            'id_document', 'proof_of_address', 'drivers_license_front', 
            'drivers_license_back', 'passport', 'utility_bill', 
            'bank_statement', 'profile_photo', 'signature'
        ];
        return fileFields.includes(fieldName.toLowerCase()) || 
               fieldName.toLowerCase().includes('document') ||
               fieldName.toLowerCase().includes('upload') ||
               fieldName.toLowerCase().includes('file');
    };

    const getFileType = (filename) => {
        if (!filename) return 'unknown';
        const ext = filename.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
        if (['pdf'].includes(ext)) return 'pdf';
        return 'document';
    };

    const renderFilePreview = (filename, fieldName) => {
        if (!filename) return null;
        
        const fileType = getFileType(filename);
        const fileUrl = `${process.env.REACT_APP_API_URL}/${filename}`;
        
        return (
            <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {fileType === 'image' && <ImageIcon color="primary" />}
                    {fileType === 'pdf' && <PictureAsPdfIcon color="error" />}
                    {fileType === 'document' && <AttachmentIcon color="action" />}
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {filename.split('/').pop()}
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => window.open(fileUrl, '_blank')}
                        variant="outlined"
                    >
                        View
                    </Button>
                </Box>
                {fileType === 'image' && (
                    <Box sx={{ 
                        width: '100%', 
                        maxWidth: 200, 
                        border: '1px solid #ddd', 
                        borderRadius: 1,
                        overflow: 'hidden'
                    }}>
                        <img 
                            src={fileUrl} 
                            alt={fieldName}
                            style={{ 
                                width: '100%', 
                                height: 'auto',
                                display: 'block'
                            }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    };

    const calculateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return age;
    };

    if (isLoadingApplication || !application) {
        return (
            <div style={{marginTop: '75px'}}>
                <Container>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </div>
        );
    }

    return (
        <div style={{marginTop: '75px'}}>
            <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                {/* Header - Mobile Responsive */}
                <Box sx={{ 
                    mb: { xs: 2, md: 3 }, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    gap: 2 
                }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/list-applications')}
                        variant="outlined"
                        size={isSmallMobile ? "small" : "medium"}
                        fullWidth={isSmallMobile}
                    >
                        Back to Applications
                    </Button>
                    
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography 
                            variant={isMobile ? "h5" : "h4"} 
                            sx={{ 
                                fontWeight: 'bold',
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                            }}
                        >
                            Application Details
                        </Typography>
                    </Box>
                    
                    <Chip 
                        label={application.status} 
                        color={getStatusColor(application.status)}
                        sx={{ 
                            textTransform: 'capitalize', 
                            fontSize: { xs: '0.875rem', md: '1rem' }, 
                            px: { xs: 1, md: 2 }, 
                            py: { xs: 0.5, md: 1 },
                            alignSelf: { xs: 'flex-start', sm: 'center' }
                        }}
                    />
                </Box>

                {updateSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        Application status updated successfully!
                    </Alert>
                )}

                {migrationSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        Login access granted successfully! Welcome email sent to user.
                    </Alert>
                )}

                <Grid container spacing={{ xs: 2, md: 3 }}>
                    {/* Left Column - Application Details */}
                    <Grid item xs={12} lg={8}>
                        {/* Personal Information */}
                        <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    mb: 3, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                                }}
                            >
                                <PersonIcon />
                                Personal Information
                            </Typography>
                            
                            <Grid container spacing={{ xs: 2, md: 3 }}>
                                {/* Full Name */}
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                color="text.secondary"
                                                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                                            >
                                                Full Name
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                sx={{ 
                                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                                    fontWeight: 'medium',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {application.name}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                
                                {/* Date of Birth / Age */}
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                                }}
                                            >
                                                <CakeIcon fontSize="small" />
                                                Date of Birth / Age
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'medium' }}
                                            >
                                                {getDate(application.date_of_birth)}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                                            >
                                                {calculateAge(application.date_of_birth)} years old
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Email */}
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                                }}
                                            >
                                                <EmailIcon fontSize="small" />
                                                Email Address
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                sx={{ 
                                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                                    fontWeight: 'medium',
                                                    wordBreak: 'break-all'
                                                }}
                                            >
                                                {application.email}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Phone */}
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                                }}
                                            >
                                                <PhoneIcon fontSize="small" />
                                                Phone Number
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                sx={{ 
                                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                                    fontWeight: 'medium'
                                                }}
                                            >
                                                {application.phone}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Nationality */}
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                                }}
                                            >
                                                <PublicIcon fontSize="small" />
                                                Nationality
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                sx={{ 
                                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                                    fontWeight: 'medium'
                                                }}
                                            >
                                                {application.nationality}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Country of Residence */}
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                                }}
                                            >
                                                <PublicIcon fontSize="small" />
                                                Country of Residence
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                sx={{ 
                                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                                    fontWeight: 'medium'
                                                }}
                                            >
                                                {application.country_of_residence}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Complete Form Data Section */}
                        {application.form_data && Object.keys(application.form_data).length > 0 && (
                            <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        mb: expandedSections.formData ? 3 : 0,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => toggleSection('formData')}
                                >
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontSize: { xs: '1.1rem', md: '1.25rem' }
                                        }}
                                    >
                                        <FolderIcon />
                                        Complete Form Data
                                    </Typography>
                                    {expandedSections.formData ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Box>
                                
                                {expandedSections.formData && (
                                    <Grid container spacing={{ xs: 2, md: 3 }}>
                                        {Object.entries(application.form_data).map(([key, value]) => {
                                            // Skip empty values and file fields (they'll be shown in documents section)
                                            if (!value || value === '' || isFileField(key)) return null;
                                            
                                            return (
                                                <Grid item xs={12} sm={6} md={4} key={key}>
                                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                                            <Typography 
                                                                variant="subtitle2" 
                                                                color="text.secondary"
                                                                sx={{ 
                                                                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                                                                    mb: 1
                                                                }}
                                                            >
                                                                {formatFieldName(key)}
                                                            </Typography>
                                                            <Typography 
                                                                variant="body1"
                                                                sx={{ 
                                                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                                                    fontWeight: 'medium',
                                                                    wordBreak: 'break-word'
                                                                }}
                                                            >
                                                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Paper>
                        )}

                        {/* Documents and Files Section */}
                        <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    mb: expandedSections.documents ? 3 : 0,
                                    cursor: 'pointer'
                                }}
                                onClick={() => toggleSection('documents')}
                            >
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontSize: { xs: '1.1rem', md: '1.25rem' }
                                    }}
                                >
                                    <AttachmentIcon />
                                    Documents & Files
                                </Typography>
                                {expandedSections.documents ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                            
                            {expandedSections.documents && (
                                <Grid container spacing={{ xs: 2, md: 3 }}>
                                    {application.form_data && Object.entries(application.form_data).map(([key, value]) => {
                                        // Only show file fields
                                        if (!isFileField(key) || !value || value === '') return null;
                                        
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={key}>
                                                <Card variant="outlined" sx={{ height: '100%' }}>
                                                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                                        <Typography 
                                                            variant="subtitle2" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                                                mb: 1
                                                            }}
                                                        >
                                                            {formatFieldName(key)}
                                                        </Typography>
                                                        {renderFilePreview(value, key)}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                    
                                    {/* Show message if no documents */}
                                    {(!application.form_data || 
                                      !Object.entries(application.form_data).some(([key, value]) => 
                                        isFileField(key) && value && value !== '')) && (
                                        <Grid item xs={12}>
                                            <Box sx={{ 
                                                textAlign: 'center', 
                                                py: 4,
                                                color: 'text.secondary'
                                            }}>
                                                <AttachmentIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                                <Typography variant="body1">
                                                    No documents uploaded
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Paper>

                        {/* Application Timeline */}
                        <Paper sx={{ p: { xs: 2, md: 3 } }}>
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    mb: expandedSections.timeline ? 3 : 0,
                                    cursor: 'pointer'
                                }}
                                onClick={() => toggleSection('timeline')}
                            >
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontSize: { xs: '1.1rem', md: '1.25rem' }
                                    }}
                                >
                                    Application Timeline
                                </Typography>
                                {expandedSections.timeline ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                            
                            {expandedSections.timeline && (
                            
                            <Box sx={{ pl: { xs: 1, md: 2 } }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                            fontWeight: 'medium',
                                            fontSize: { xs: '0.9rem', md: '1rem' }
                                        }}
                                    >
                                        Application Submitted
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                                    >
                                        {getDateTime(application.created_at)}
                                    </Typography>
                                </Box>

                                {application.email_sent_at && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography 
                                            variant="subtitle1" 
                                            sx={{ 
                                                fontWeight: 'medium',
                                                fontSize: { xs: '0.9rem', md: '1rem' }
                                            }}
                                        >
                                            Confirmation Email Sent
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                                        >
                                            {getDateTime(application.email_sent_at)}
                                        </Typography>
                                    </Box>
                                )}

                                {application.registration_token && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography 
                                            variant="subtitle1" 
                                            sx={{ 
                                                fontWeight: 'medium',
                                                fontSize: { xs: '0.9rem', md: '1rem' }
                                            }}
                                        >
                                            Registration Token
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                                        >
                                            Expires: {getDateTime(application.registration_token_expires_at)}
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            color={new Date(application.registration_token_expires_at) > new Date() ? 'success.main' : 'error.main'}
                                            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                                        >
                                            {new Date(application.registration_token_expires_at) > new Date() ? 'Valid' : 'Expired'}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ mb: 2 }}>
                                    <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                            fontWeight: 'medium',
                                            fontSize: { xs: '0.9rem', md: '1rem' }
                                        }}
                                    >
                                        Last Updated
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                                    >
                                        {getDateTime(application.updated_at)}
                                    </Typography>
                                </Box>
                            </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Right Column - Status Management */}
                    <Grid item xs={12} lg={4}>
                        <Paper sx={{ 
                            p: { xs: 2, md: 3 }, 
                            position: { lg: 'sticky' }, 
                            top: { lg: 100 }
                        }}>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    mb: 3,
                                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                                }}
                            >
                                Manage Application
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <FormControl 
                                    fullWidth 
                                    sx={{ mb: 2 }}
                                    size={isSmallMobile ? "small" : "medium"}
                                >
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={status}
                                        label="Status"
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="processing">Processing</MenuItem>
                                        <MenuItem value="approved">Approved</MenuItem>
                                        <MenuItem value="rejected">Rejected</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={isSmallMobile ? 3 : 4}
                                    label="Notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add notes about this application..."
                                    size={isSmallMobile ? "small" : "medium"}
                                />
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleStatusUpdate}
                                disabled={updateStatusMutation.isLoading}
                                sx={{ 
                                    mb: 2,
                                    py: { xs: 1, md: 1.5 }
                                }}
                                size={isSmallMobile ? "medium" : "large"}
                            >
                                {updateStatusMutation.isLoading ? 'Updating...' : 'Update Status'}
                            </Button>

                            {/* Grant Login Access Button */}
                            <Button
                                fullWidth
                                variant={migrationEligibility?.can_migrate ? "contained" : "outlined"}
                                color={migrationEligibility?.can_migrate ? "primary" : "inherit"}
                                startIcon={migrationEligibility?.user_exists ? <PersonIcon /> : <LockOpenIcon />}
                                onClick={() => setShowMigrationDialog(true)}
                                disabled={isMigrationDisabled()}
                                sx={{ 
                                    mb: 2,
                                    py: { xs: 1, md: 1.5 },
                                    backgroundColor: migrationEligibility?.can_migrate ? '#0d9153' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: migrationEligibility?.can_migrate ? '#0a7844' : 'rgba(255, 255, 255, 0.08)',
                                    }
                                }}
                                size={isSmallMobile ? "medium" : "large"}
                            >
                                {getMigrationButtonText()}
                            </Button>

                            <Divider sx={{ my: 2 }} />

                            <Box>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                        mb: 1,
                                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                                    }}
                                >
                                    Application ID
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ 
                                        mb: 2,
                                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                                    }}
                                >
                                    {application.id}
                                </Typography>

                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                        mb: 1,
                                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                                    }}
                                >
                                    Application Type
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    {application.application_type.replace('_', ' ')}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Migration Confirmation Dialog */}
                <Dialog 
                    open={showMigrationDialog} 
                    onClose={() => setShowMigrationDialog(false)}
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: { 
                            borderRadius: 2,
                            p: { xs: 1, md: 2 }
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockOpenIcon sx={{ color: '#0d9153' }} />
                            Grant Login Access
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" paragraph>
                            This will create a user account for <strong>{application?.name}</strong> and send them 
                            login credentials via email.
                        </Typography>
                        <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1, mb: 2 }}>
                            <Typography variant="subtitle2" color="info.dark" gutterBottom>
                                What will happen:
                            </Typography>
                            <Typography variant="body2" color="info.dark" component="div">
                                • User account will be created with verified status<br/>
                                • Temporary password will be generated<br/>
                                • Welcome email with login credentials will be sent<br/>
                                • Application status will be updated to "migrated_to_user"<br/>
                                • User can immediately access the investment dashboard
                            </Typography>
                        </Box>
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Make sure the application is approved and all information is correct before granting login access.
                            </Typography>
                        </Alert>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, md: 3 }, pt: 1 }}>
                        <Button 
                            onClick={() => setShowMigrationDialog(false)}
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleMigrationConfirm}
                            variant="contained" 
                            color="primary"
                            disabled={migrationMutation.isLoading}
                            startIcon={migrationMutation.isLoading ? <CircularProgress size={20} /> : <LockOpenIcon />}
                            sx={{
                                backgroundColor: '#0d9153',
                                '&:hover': {
                                    backgroundColor: '#0a7844',
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                }
                            }}
                        >
                            {migrationMutation.isLoading ? 'Creating Account...' : 'Grant Access'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </div>
    );
} 